import { useEffect } from "react";

import {
  SESSION_EXPIRED_ERRORCODE,
  useLaunchBrokenFlow,
  useLaunchSessionExpired,
} from "../AppErrorProvider";
import type { GraphQlError, OperationResult } from "./types";
import {
  isNullValueErrorDerivedFromOtherError,
  useRequestErrorTracker,
} from "./useErrorTracker";

const NOT_DEFINED = "NOT_DEFINED";
const HTTP_CODE_NOT_DEFINED = 418;

const hasBrokenFlowCodes = <ClientErrorResult>(
  error: GraphQlError<ClientErrorResult>,
  discardedErrorCodes?: Array<string>,
) =>
  !error.extensions?.errorCode ||
  (!discardedErrorCodes?.includes?.(error.extensions?.errorCode) &&
    error.extensions?.errorCode !== SESSION_EXPIRED_ERRORCODE);
const hasBrokenFlowOrSessionExpiredCodes = <ClientErrorResult>(
  error: GraphQlError<ClientErrorResult>,
  discardedErrorCodes?: Array<string>,
) =>
  !error.extensions?.errorCode ||
  !discardedErrorCodes?.includes?.(error.extensions?.errorCode);

const shouldLaunchBrokenFlow = <ClientErrorResult>(
  errors?: Array<GraphQlError<ClientErrorResult>> | null,
  discardedErrorCodes?: Array<string>,
) =>
  Array.isArray(errors) &&
  errors?.length &&
  errors?.some((error) => hasBrokenFlowCodes(error, discardedErrorCodes));

const shouldLaunchBrokenFlowOrSessionExpired = <ClientErrorResult>(
  errors?: Array<GraphQlError<ClientErrorResult>> | null,
  discardedErrorCodes?: Array<string>,
) =>
  Array.isArray(errors) &&
  errors?.length &&
  errors?.some((error) =>
    hasBrokenFlowOrSessionExpiredCodes(error, discardedErrorCodes),
  );

const shouldLaunchSessionExpired = <ClientErrorResult>(
  errors?: Array<GraphQlError<ClientErrorResult>> | null,
) =>
  Array.isArray(errors) &&
  errors?.length &&
  errors?.some?.(
    (error) => error.extensions?.errorCode === SESSION_EXPIRED_ERRORCODE,
  );

const useFrontendApiBlockingError = <ClientResult, ClientErrorResult>(
  result: OperationResult<ClientResult, ClientErrorResult>,
  discardedErrorCodes?: Array<string>,
  namespace?: string,
) => {
  const launchBrokenFlow = useLaunchBrokenFlow();
  const launchSessionExpired = useLaunchSessionExpired();
  const logRequestError = useRequestErrorTracker(namespace);
  const { errors, httpStatusCode, operationName } = result || {};

  useEffect(() => {
    if (
      shouldLaunchBrokenFlowOrSessionExpired<ClientErrorResult>(
        errors,
        discardedErrorCodes,
      )
    ) {
      errors?.forEach((error) => {
        if (
          hasBrokenFlowOrSessionExpiredCodes<ClientErrorResult>(
            error,
            discardedErrorCodes,
          ) &&
          !isNullValueErrorDerivedFromOtherError(
            errors,
            error.extensions?.errorCode,
          )
        ) {
          logRequestError({
            blockableFlow: true,
            errorMessage: error.message,
            edoErrorCode: error.extensions?.errorCode || NOT_DEFINED,
            responseHttpCode: httpStatusCode || HTTP_CODE_NOT_DEFINED,
            graphqlOperationName: operationName || NOT_DEFINED,
          });
        }
      });
    }
  }, [
    errors,
    httpStatusCode,
    operationName,
    discardedErrorCodes,
    logRequestError,
  ]);

  useEffect(() => {
    if (
      shouldLaunchBrokenFlow<ClientErrorResult>(
        result?.errors,
        discardedErrorCodes,
      )
    )
      launchBrokenFlow();
  }, [launchBrokenFlow, discardedErrorCodes, result]);

  useEffect(() => {
    if (shouldLaunchSessionExpired<ClientErrorResult>(result?.errors))
      launchSessionExpired();
  }, [discardedErrorCodes, result, launchSessionExpired]);
};

export default useFrontendApiBlockingError;
